import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import ArrowBackIcon from "@material-ui/icons/ArrowBackIosOutlined";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip as RTooltip, Legend, CartesianGrid,
} from "recharts";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { allowedForms, toNumber, toTable, toSeries, toPivot, measureLabel } from "./reportForms";

const T = { bg: "#F6F7F9", panel: "#FFFFFF", line: "#E6E9ED", ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4" };
const COLORS = ["#1E9E62", "#3B82C4", "#C77A12", "#8B5CF6", "#C0453B", "#0EA5A5", "#D98BB0", "#6B7280"];
const OPS = [
  { value: "=", label: "igual" }, { value: "≠", label: "diferente" }, { value: "contém", label: "contém" },
  { value: ">", label: "maior" }, { value: "<", label: "menor" }, { value: "vazio", label: "vazio" },
];
const MEASURES = [
  { value: "contagem", label: "contagem" }, { value: "soma", label: "soma" }, { value: "media", label: "média" },
  { value: "minimo", label: "mínimo" }, { value: "maximo", label: "máximo" }, { value: "contagem_distinta", label: "distintos" },
];
const PERIODS = [
  { value: "", label: "Tudo" }, { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" }, { value: "90d", label: "Últimos 90 dias" },
];

const useStyles = makeStyles(theme => ({
  page: { background: T.bg, minHeight: "100%" },
  wrap: { maxWidth: 1240, margin: "0 auto", padding: theme.spacing(3) },
  top: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  title: { fontFamily: theme.app.heading, fontSize: 20, fontWeight: 700, color: theme.app.ink },
  grow: { flex: 1 },
  grid: { display: "grid", gridTemplateColumns: "320px 1fr", gap: theme.spacing(3), alignItems: "start" },
  panel: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: theme.spacing(2) },
  section: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: T.ink3, margin: theme.spacing(2, 0, 1) },
  row: { display: "flex", alignItems: "center", gap: 6, marginBottom: 8 },
  chartBox: { height: 320, marginTop: theme.spacing(2) },
  bigNum: { fontSize: 56, fontWeight: 800, color: T.ink, textAlign: "center", padding: theme.spacing(4, 0) },
  th: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: T.ink3, fontWeight: 600, whiteSpace: "nowrap" },
  td: { fontSize: 13.5, color: T.ink, whiteSpace: "nowrap" },
  tableWrap: { border: `1px solid ${T.line}`, borderRadius: 10, overflowX: "auto", marginTop: theme.spacing(2) },
  hint: { fontSize: 12.5, color: T.ink3, marginTop: 6 },
}));

const ReportBuilder = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id, rid } = useParams(); // id = datasetId; rid = reportId (edição)

  const [ds, setDs] = useState(null);
  const [name, setName] = useState("Novo relatório");
  const [spec, setSpec] = useState({ periodo: null, filtros: [], dimensoes: [], medidas: [{ tipo: "contagem" }], visual: { forma: "tabela" } });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/datasets/${id}`).then(({ data }) => setDs(data.dataset)).catch(toastError);
  }, [id]);

  useEffect(() => {
    if (!rid) return;
    api.get(`/reports/${rid}`).then(({ data }) => {
      setName(data.report.name);
      setSpec({ periodo: null, filtros: [], dimensoes: [], medidas: [{ tipo: "contagem" }], visual: { forma: "tabela" }, ...data.report.spec });
    }).catch(toastError);
  }, [rid]);

  const fields = (ds && ds.fields) || [];
  const forms = allowedForms(spec, fields);

  // roda o recorte (preview ao vivo) sempre que a spec muda
  const run = useCallback(async () => {
    try {
      const { data } = await api.post(`/datasets/${id}/summary`, spec);
      setResult(data);
    } catch (err) {
      toastError(err);
    }
  }, [id, spec]);
  useEffect(() => { run(); }, [run]);

  // se a forma escolhida deixou de ser válida, cai pra tabela
  useEffect(() => {
    if (!forms.some(f => f.value === spec.visual.forma)) {
      setSpec(s => ({ ...s, visual: { ...s.visual, forma: "tabela" } }));
    }
  }, [forms, spec.visual.forma]);

  const patchSpec = p => setSpec(s => ({ ...s, ...p }));
  const setDim = (i, campo) => {
    const dims = spec.dimensoes.slice();
    const f = fields.find(x => x.name === campo);
    const isTime = campo === "createdAt" || (f && f.type === "data");
    dims[i] = campo ? { campo, ...(isTime ? { granularidade: (dims[i] && dims[i].granularidade) || "dia" } : {}) } : null;
    patchSpec({ dimensoes: dims.filter(Boolean) });
  };
  const setGran = (i, g) => { const dims = spec.dimensoes.slice(); dims[i] = { ...dims[i], granularidade: g }; patchSpec({ dimensoes: dims }); };
  const setMeasure = (tipo, campo) => patchSpec({ medidas: [{ tipo, ...(tipo !== "contagem" ? { campo } : {}) }] });

  const addFilter = () => patchSpec({ filtros: [...spec.filtros, { campo: "", op: "=", valor: "" }] });
  const setFilter = (i, patch) => { const fs = spec.filtros.slice(); fs[i] = { ...fs[i], ...patch }; patchSpec({ filtros: fs }); };
  const rmFilter = i => patchSpec({ filtros: spec.filtros.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      if (rid) {
        await api.patch(`/reports/${rid}`, { name, spec });
        toast.success("Relatório salvo.");
      } else {
        await api.post("/reports", { datasetId: Number(id), name, spec });
        toast.success("Relatório criado.");
      }
      history.push(`/datasets/${id}/reports`);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const measure = (spec.medidas && spec.medidas[0]) || { tipo: "contagem" };
  const numFields = fields.filter(f => f.type === "numero");
  const dimOptions = [{ name: "createdAt", label: "Criado em" }, ...fields.map(f => ({ name: f.name, label: f.label || f.name }))];

  const renderChart = () => {
    if (!result) return null;
    const forma = spec.visual.forma;
    if (forma === "numero") return <div className={classes.bigNum}>{toNumber(result)}</div>;
    if (forma === "barras" || forma === "linha" || forma === "pizza") {
      const series = toSeries(result);
      if (forma === "pizza") {
        if (series.length > 5) return <div className={classes.hint}>Muitas fatias ({series.length}) para uma pizza — veja a tabela abaixo.</div>;
        return (
          <div className={classes.chartBox}>
            <ResponsiveContainer><PieChart>
              <Pie data={series} dataKey="valor" nameKey="name" outerRadius={110} label>
                {series.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Legend /><RTooltip />
            </PieChart></ResponsiveContainer>
          </div>
        );
      }
      return (
        <div className={classes.chartBox}>
          <ResponsiveContainer>
            {forma === "linha" ? (
              <LineChart data={series}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><RTooltip /><Line type="monotone" dataKey="valor" stroke={COLORS[0]} strokeWidth={2} /></LineChart>
            ) : (
              <BarChart data={series}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><RTooltip /><Bar dataKey="valor" fill={COLORS[0]} /></BarChart>
            )}
          </ResponsiveContainer>
        </div>
      );
    }
    if (forma === "barras_empilhadas") {
      const piv = toPivot(result);
      return (
        <div className={classes.chartBox}>
          <ResponsiveContainer><BarChart data={piv.data}>
            <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><RTooltip /><Legend />
            {piv.cols.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={COLORS[i % COLORS.length]} />)}
          </BarChart></ResponsiveContainer>
        </div>
      );
    }
    if (forma === "tabela_cruzada") {
      const piv = toPivot(result);
      return (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead><TableRow><TableCell className={classes.th} /> {piv.cols.map(c => <TableCell key={c} className={classes.th}>{c}</TableCell>)}</TableRow></TableHead>
            <TableBody>{piv.data.map(r => (
              <TableRow key={r.name}><TableCell className={classes.td} style={{ fontWeight: 600 }}>{r.name}</TableCell>{piv.cols.map(c => <TableCell key={c} className={classes.td}>{r[c] || 0}</TableCell>)}</TableRow>
            ))}</TableBody>
          </Table>
        </div>
      );
    }
    return null;
  };

  const flat = result ? toTable(result, spec, fields) : { cols: [], rows: [] };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.top}>
          <IconButton size="small" onClick={() => history.push(`/datasets/${id}/reports`)}><ArrowBackIcon fontSize="small" /></IconButton>
          <TextField value={name} onChange={e => setName(e.target.value)} variant="standard" InputProps={{ disableUnderline: true, style: { fontSize: 20, fontWeight: 700 } }} />
          <div className={classes.grow} />
          <Button variant="contained" color="primary" disableElevation onClick={save} disabled={saving}>{saving ? "Salvando…" : "Salvar relatório"}</Button>
        </div>

        <div className={classes.grid}>
          {/* controles */}
          <div className={classes.panel}>
            <div className={classes.section}>Período</div>
            <TextField select fullWidth size="small" variant="outlined" value={(spec.periodo && spec.periodo.valor) || ""} onChange={e => patchSpec({ periodo: e.target.value ? { campo: "createdAt", tipo: "relativo", valor: e.target.value } : null })}>
              {PERIODS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>

            <div className={classes.section}>Filtros</div>
            {spec.filtros.map((f, i) => (
              <div key={i} className={classes.row}>
                <TextField select size="small" variant="outlined" value={f.campo} onChange={e => setFilter(i, { campo: e.target.value })} style={{ flex: 1 }}>
                  {dimOptions.map(o => <MenuItem key={o.name} value={o.name}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" variant="outlined" value={f.op} onChange={e => setFilter(i, { op: e.target.value })} style={{ width: 90 }}>
                  {OPS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                {f.op !== "vazio" && <TextField size="small" variant="outlined" value={f.valor || ""} onChange={e => setFilter(i, { valor: e.target.value })} style={{ width: 90 }} />}
                <IconButton size="small" onClick={() => rmFilter(i)}><DeleteIcon fontSize="small" /></IconButton>
              </div>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addFilter} style={{ textTransform: "none" }}>Filtro</Button>

            <div className={classes.section}>Agrupar por</div>
            {[0, 1].map(i => {
              const dim = spec.dimensoes[i];
              const isTime = dim && (dim.campo === "createdAt" || (fields.find(x => x.name === dim.campo) || {}).type === "data");
              return (
                <div key={i} className={classes.row}>
                  <TextField select size="small" variant="outlined" label={i === 0 ? "Dimensão 1" : "Dimensão 2"} value={(dim && dim.campo) || ""} onChange={e => setDim(i, e.target.value)} style={{ flex: 1 }}>
                    <MenuItem value="">— nenhuma —</MenuItem>
                    {dimOptions.map(o => <MenuItem key={o.name} value={o.name}>{o.label}</MenuItem>)}
                  </TextField>
                  {isTime && (
                    <TextField select size="small" variant="outlined" value={dim.granularidade || "dia"} onChange={e => setGran(i, e.target.value)} style={{ width: 90 }}>
                      <MenuItem value="dia">dia</MenuItem><MenuItem value="mes">mês</MenuItem><MenuItem value="ano">ano</MenuItem>
                    </TextField>
                  )}
                </div>
              );
            })}

            <div className={classes.section}>Medida</div>
            <div className={classes.row}>
              <TextField select size="small" variant="outlined" value={measure.tipo} onChange={e => setMeasure(e.target.value, measure.campo)} style={{ flex: 1 }}>
                {MEASURES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              {measure.tipo !== "contagem" && (
                <TextField select size="small" variant="outlined" label="campo" value={measure.campo || ""} onChange={e => setMeasure(measure.tipo, e.target.value)} style={{ flex: 1 }}>
                  {numFields.map(f => <MenuItem key={f.name} value={f.name}>{f.label || f.name}</MenuItem>)}
                </TextField>
              )}
            </div>

            <div className={classes.section}>Forma</div>
            <TextField select fullWidth size="small" variant="outlined" value={spec.visual.forma} onChange={e => patchSpec({ visual: { ...spec.visual, forma: e.target.value } })}>
              {forms.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
            </TextField>
            <div className={classes.hint}>A tabela aparece sempre; o gráfico é uma visão adicional.</div>
          </div>

          {/* preview */}
          <div className={classes.panel}>
            <Typography style={{ fontWeight: 600, color: T.ink }}>{name}</Typography>
            {renderChart()}
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead><TableRow>{flat.cols.map(c => <TableCell key={c.key} className={classes.th}>{c.label}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {flat.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={flat.cols.length || 1} className={classes.td} style={{ color: T.ink3, textAlign: "center", padding: 24 }}>Sem dados para este recorte.</TableCell></TableRow>
                  ) : flat.rows.map((r, i) => (
                    <TableRow key={i}>{flat.cols.map(c => <TableCell key={c.key} className={classes.td}>{r[c.key]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
